import { Adapt, Label, RadioGroup, Select, Switch, Text, XStack } from '@my/ui'
import { Check, ChevronDown } from '@tamagui/lucide-icons'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { DateTime } from 'luxon'
import { Sheet } from 'tamagui'
import { Period } from '../../types/time'
import { convertTo12Hour, convertTo24Hour } from '../../utils/timeUtils'


interface TimeSelectorProps {
  time: string | null
  onChange: (time: string | null) => void
  selectedDate: string | null
}

type TimeMode = 'specific' | 'anytime'

export function TimeSelector({ time, onChange, selectedDate }: TimeSelectorProps) {
  // Ref to track if we're handling an internal update
  const internalUpdateRef = useRef(false)
  // Ref to track the previous time prop to detect external changes
  const prevTimeRef = useRef(time)
  
  const nextHour = DateTime.now().plus({ hours: 1 }).startOf('hour')
  const defaultTime = convertTo12Hour(nextHour.toFormat('HH:mm'))

  // Initialize state from props or defaults
  const [timeMode, setTimeMode] = useState<TimeMode>(() => time ? 'specific' : 'anytime')
  const [internalHour, setInternalHour] = useState(() =>
    time ? convertTo12Hour(time).hour : defaultTime.hour
  )
  const [internalMinute, setInternalMinute] = useState(() =>
    time ? convertTo12Hour(time).minute : defaultTime.minute
  )
  const [internalPeriod, setInternalPeriod] = useState<Period>(() =>
    time ? convertTo12Hour(time).period : defaultTime.period
  )

  // Memoized handler for time mode changes to prevent recreation on renders
  const handleTimeModeChange = useCallback((value: string) => {
    const newMode = value as TimeMode
    setTimeMode(newMode)
    
    // If changing to anytime, immediately notify parent
    if (newMode === 'anytime') {
      internalUpdateRef.current = true
      onChange(null)
    }
  }, [onChange])

  // Memoized handler for period changes
  const handlePeriodChange = useCallback((value: string) => {
    setInternalPeriod(value as Period)
  }, [])

  // Handle external prop changes
  useEffect(() => {
    // Skip if we're in the middle of our own update cycle
    if (internalUpdateRef.current) {
      internalUpdateRef.current = false
      return
    }

    // Only process if time has actually changed from previous value
    if (time !== prevTimeRef.current) {
      prevTimeRef.current = time;
      
      if (time) {
        const { hour, minute, period } = convertTo12Hour(time)
        setInternalHour(hour)
        setInternalMinute(minute)
        setInternalPeriod(period)
        setTimeMode('specific')
      } else {
        setTimeMode('anytime')
      }
    }
  }, [time])

  // Handle specific time updates and sync to parent
  useEffect(() => {
    // Skip initial render and anytime mode
    if (timeMode !== 'specific' || !internalHour) {
      return;
    }

    // Use a timeout to debounce multiple rapid state changes
    const timeoutId = setTimeout(() => {
      const timeString = convertTo24Hour(internalHour, internalMinute, internalPeriod as Period)
      internalUpdateRef.current = true
      prevTimeRef.current = timeString
      onChange(timeString)
    }, 0)

    return () => clearTimeout(timeoutId)
  }, [timeMode, internalHour, internalMinute, internalPeriod, onChange])

  // Handle date clearing effect
  useEffect(() => {
    if (!selectedDate && timeMode === 'specific') {
      setTimeMode('anytime')
      internalUpdateRef.current = true
      prevTimeRef.current = null
      onChange(null)
    }
  }, [selectedDate, onChange, timeMode])

  return (
    <>
      <Text fontSize="$7" fontWeight="bold" marginTop="$6">
        When <Text color="$color7" fontSize="$4" fontWeight="normal">(What time do you want to start?)</Text>
      </Text>

      {!selectedDate ? (
        <Text color="$color8" fontSize="$3" marginTop="$2">
          Set a date first to specify a start time
        </Text>
      ) : (
        <>
          <RadioGroup 
            value={timeMode} 
            onValueChange={handleTimeModeChange}
            name="timeMode"
            alignItems="center"
          >
            <XStack gap="$5">
              <XStack alignItems="center" gap="$2" cursor="pointer">
                <RadioGroup.Item value="anytime" id="anytime" 
                  size="$3"
                  backgroundColor={timeMode === 'anytime' ? '$accentColor' : '$color1'} 
                  focusStyle={timeMode === 'anytime' ? { backgroundColor: '$accentColor' } : { backgroundColor: '$color1' }} 
                  hoverStyle={timeMode === 'anytime' ? { backgroundColor: '$accentColor' } : { backgroundColor: '$color1' }}
                  >
                  <RadioGroup.Indicator backgroundColor='$color1' scale={1.2} unstyled height={6} width={6} borderRadius="$4" />
                </RadioGroup.Item>
                <Label htmlFor="anytime" cursor="pointer">Anytime</Label>
              </XStack>

              <XStack alignItems="center" gap="$2" cursor="pointer">
                <RadioGroup.Item value="specific" id="specific" 
                  size="$3"
                  backgroundColor={timeMode === 'specific' ? '$accentColor' : '$color1'} 
                  focusStyle={timeMode === 'specific' ? { backgroundColor: '$accentColor' } : { backgroundColor: '$color1' }} 
                  hoverStyle={timeMode === 'specific' ? { backgroundColor: '$accentColor' } : { backgroundColor: '$color1' }}
                  >
                  <RadioGroup.Indicator backgroundColor='$color1' scale={1.2} unstyled height={6} width={6} borderRadius="$4" />
                </RadioGroup.Item>
                <Label htmlFor="specific" cursor="pointer">Specific Time</Label>
              </XStack>
            </XStack>
          </RadioGroup>

          {timeMode === 'specific' && (
            <XStack  
              borderColor="$accentColor"
              borderWidth={4}
              borderRadius="$6" 
              marginBottom="$4"
              padding="$1.5"
              alignSelf="center"
            >
              <Select 
                value={timeMode === 'specific' ? internalHour ?? '' : ''} 
                onValueChange={(newHour) => {
                  setInternalHour(newHour)
                }}
              >
                <Select.Trigger 
                  width={78} 
                  borderWidth={0} 
                  marginRight={0}
                  disabled={timeMode !== 'specific'}
                >
                  <Select.Value placeholder="HH" fontSize="$4" fontWeight="bold" />
                  <ChevronDown size={16} strokeWidth={3} color="$color6" />
                </Select.Trigger>
                <SelectContent>
                  {Array.from({ length: 12 }, (_, i) => (
                    <Select.Item key={i} index={i} value={(i + 1).toString().padStart(2, '0')} cursor='pointer'>
                      <Select.ItemText fontSize="$4" fontWeight="bold">{(i + 1).toString().padStart(2, '0')}</Select.ItemText>
                      <Select.ItemIndicator>
                        <Check size={16} />
                      </Select.ItemIndicator>
                    </Select.Item>
                  ))}
                </SelectContent>
              </Select>
              <Text fontSize="$5" fontWeight="bold" marginTop="$2.5">:</Text>
              <Select 
                value={timeMode === 'specific' ? internalMinute ?? '00' : '00'} 
                onValueChange={setInternalMinute}
                defaultValue='00'
              >
                <Select.Trigger 
                  width={78} 
                  borderWidth={0}
                  disabled={timeMode !== 'specific'}
                >
                  <Select.Value fontSize="$4" fontWeight="bold" />
                  <ChevronDown size={16} strokeWidth={3} color="$color6" />
                </Select.Trigger>
                <SelectContent>
                  {Array.from({ length: 12 }, (_, i) => (
                    <Select.Item key={i} index={i} value={(i * 5).toString().padStart(2, '0')} cursor='pointer'>
                      <Select.ItemText fontSize="$4" fontWeight="bold">
                        {(i * 5).toString().padStart(2, '0')}
                      </Select.ItemText>
                      <Select.ItemIndicator>
                        <Check size={16} />
                      </Select.ItemIndicator>
                    </Select.Item>
                  ))}
                </SelectContent>
              </Select>
              <XStack 
                alignItems="center" 
                gap="$2"
                cursor="pointer"
                opacity={timeMode === 'specific' ? 1 : 0.5}
                pressStyle={{ opacity: 0.8 }}
                onPress={(e: any) => {
                  if (timeMode === 'specific') {
                    handlePeriodChange(internalPeriod === 'AM' ? 'PM' : 'AM')
                  }
                }}
                {...{
                  animation: 'quick',
                  flexDirection: 'row',
                }}
              >
                <Text 
                  color={internalPeriod === 'AM' ? '$color12' : '$color5'} 
                  fontSize="$4" 
                  fontWeight="bold"
                >
                  AM
                </Text>
                
                <Switch
                  size="$1.5"
                  cursor="pointer"
                  backgroundColor="$color6"
                  checked={internalPeriod === 'PM'}
                  onCheckedChange={(checked) => {
                    handlePeriodChange(checked ? 'PM' : 'AM')
                  }}
                  disabled={timeMode !== 'specific'}
                >
                  <Switch.Thumb animation="200ms" backgroundColor="$color1" scale={0.7} />
                </Switch>

                <Text 
                  color={internalPeriod === 'PM' ? '$color12' : '$color5'} 
                  fontSize="$4" 
                  fontWeight="bold"
                >
                  PM
                </Text>
              </XStack>
            </XStack>
          )}
        </>
      )}
    </>
  )
}

function SelectContent({ children }: { children: React.ReactNode }) {
  return (
    <>
      <Adapt platform="touch">
        <Select.Sheet modal dismissOnSnapToBottom animation="200ms">
          <Select.Sheet.Frame>
            <Select.Sheet.ScrollView>
              <Adapt.Contents />
            </Select.Sheet.ScrollView>
          </Select.Sheet.Frame>
          <Select.Sheet.Overlay />
        </Select.Sheet>
      </Adapt>
      <Select.Content zIndex={200000}>
        <Select.ScrollUpButton />
        <Select.Viewport>
          <Select.Group>{children}</Select.Group>
        </Select.Viewport>
        <Select.ScrollDownButton />
      </Select.Content>
    </>
  )
}
